const plpCardSliderAttr = {
  container: 'data-plp-card-slider-container',
  item: 'data-plp-card-slider-item',
  clone: 'data-plp-card-slider-clone',
  prev: 'data-plp-card-slider-prev',
  next: 'data-plp-card-slider-next',
};

if (!customElements.get('plp-card-slider')) {
  customElements.define('plp-card-slider', class extends HTMLElement {
    connectedCallback() {
      // connectedCallback runs again if the card is moved around in the DOM
      if (this.isSetUp) return;

      this.container = this.querySelector(`[${plpCardSliderAttr.container}]`);
      if (!this.container) return;

      this.isSetUp = true;

      this.querySelector(`[${plpCardSliderAttr.prev}]`)?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.container.scrollBy({ left: -this.container.clientWidth, behavior: 'smooth' });
      });

      this.querySelector(`[${plpCardSliderAttr.next}]`)?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.container.scrollBy({ left: this.container.clientWidth, behavior: 'smooth' });
      });

      const items = this.container.querySelectorAll(`[${plpCardSliderAttr.item}]`);
      this.total = items.length;
      if (this.total < 2) return;

      this.style.setProperty('--plp-slider-total', this.total);
      this.style.setProperty('--plp-slider-progress', 0);

      // A copy of the first image after the last one: scrolling past the end lands on the
      // copy, and once the scroll settles we return to the real first image, so the images
      // keep coming instead of stopping at the last one.
      //
      // Nothing is copied in front of the first image. That would mean resting at a computed
      // offset, and the grid columns are fractional widths - the resting position then lands
      // mid-pixel and a 1px sliver of the neighbouring image bleeds in along the card edge.
      // 0 is the only scroll position this ever sets, and it is exact.
      const $clone = items[0].cloneNode(true);
      $clone.setAttribute(plpCardSliderAttr.clone, '');
      $clone.setAttribute('aria-hidden', 'true');
      $clone.setAttribute('tabindex', '-1');
      this.container.append($clone);

      this.container.addEventListener('scroll', () => {
        this.updateProgress();
        this.scheduleSettle();
      });

      if ('onscrollend' in window) {
        this.container.addEventListener('scrollend', () => this.settle());
      }

      // Returning to the start mid-swipe would yank the images out from under the finger.
      // Touch events rather than pointer events: the browser fires pointercancel the moment
      // it takes the gesture over for scrolling, which is exactly when we still need to know
      // the finger is down.
      this.container.addEventListener('touchstart', () => {
        this.isTouching = true;
      }, { passive: true });

      ['touchend', 'touchcancel'].forEach((name) => {
        this.container.addEventListener(name, () => {
          this.isTouching = false;
          this.scheduleSettle();
        }, { passive: true });
      });
    }

    // Distance from one image to the next, measured off the slides themselves. Comes out as
    // 0 when they aren't laid out side by side - the compact desktop grid stacks them, and a
    // card that is still hidden has no width - which parks the handlers below.
    get itemWidth() {
      const children = this.container.children;
      return children[1].offsetLeft - children[0].offsetLeft;
    }

    scheduleSettle() {
      clearTimeout(this.settleTimeout);
      this.settleTimeout = setTimeout(() => this.settle(), 150);
    }

    // Scrolling stopped - if it stopped on the copy, return to the real first image.
    settle() {
      clearTimeout(this.settleTimeout);

      const itemWidth = this.itemWidth;
      if (!itemWidth || this.isTouching) return;

      if (Math.round(this.container.scrollLeft / itemWidth) >= this.total) {
        this.container.scrollLeft = 0;
        this.updateProgress();
      }
    }

    updateProgress() {
      const itemWidth = this.itemWidth;
      if (!itemWidth) return;

      const position = Math.min(this.container.scrollLeft / itemWidth, this.total - 1);
      this.style.setProperty('--plp-slider-progress', position / (this.total - 1));
    }
  });
}
